/**
 * Capa de aplicación para sincronización de Customer.
 * Orquesta preflight + pull usando el puerto PreflightPort.
 */
import { getCustomerLastPullAt } from './pullDownHelpers';
import { devLog, devWarn } from '../../shared/dev/devLog';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { fetchRemoteCustomersSnapshot, type CustomersSnapshotPayload } from './customersRpcClient';
import {
  getWebCustomerSnapshotGeneration,
  applyWebCustomerRemoteSnapshot,
  getWebCustomerSnapshotCache,
  loadWebCustomerSnapshot,
} from '../../customers/infra/webCustomerSnapshotCache';
import { CUSTOMER_LAST_PULL_AT_KEY, recordSyncPullTimestamp } from './syncTimestamps';
import { SyncPullFailedError } from './syncPullErrors';
import { supabase } from '../../shared/supabase/supabaseClient';
import { publishCustomerSynced } from './customerSyncListeners';

/**
 * Conteos del snapshot remoto (ADR-017: sin mirror SQLite).
 */
function snapshotCounts(payload: CustomersSnapshotPayload): {
  customersCount: number;
  customerMovementsCount: number;
  salePaymentAllocationsCount: number;
} {
  return {
    customersCount: payload.customers.length,
    customerMovementsCount: payload.customer_movements.length,
    salePaymentAllocationsCount: payload.sale_payment_allocations.length,
  };
}

/**
 * Sincroniza datos de Customer desde Supabase a cache RAM (ADR-017 online-only).
 */
export async function pullCustomerDataFromSupabaseApplication(opts?: {
  force?: boolean;
}): Promise<boolean> {
  if (opts?.force) {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw new SyncPullFailedError('fiado', 'No hay sesión activa.');
    }
  }

  const online = await isOnline();
  let remoteHadChanges = false;
  let result: {
    data: {
      customersCount: number;
      customerMovementsCount: number;
      salePaymentAllocationsCount: number;
    };
    source: 'remote' | 'local';
  };

  if (!online) {
    devLog('[fiadoPullApplication] Offline, usando datos locales de Customer');
    if (opts?.force) {
      throw new SyncPullFailedError('fiado', 'Sin conexión. No se pudieron actualizar los datos de clientes.');
    }
    result = {
      data: {
        customersCount: 0,
        customerMovementsCount: 0,
        salePaymentAllocationsCount: 0,
      },
      source: 'local',
    };
  } else {
    try {
      let sinceIso: string | null = null;
      if (!opts?.force) {
        sinceIso = await getCustomerLastPullAt();
        // Cold start: si no hay cache en RAM, forzar pull completo aunque
        // exista un cursor en localStorage. Un incremental sin base en RAM
        // se descarta (applyWebCustomerRemoteSnapshot lo rechaza) y la lista
        // queda vacía hasta el siguiente refresh manual.
        if (!getWebCustomerSnapshotCache()) {
          sinceIso = null;
        }
      }

      const pullGeneration = getWebCustomerSnapshotGeneration();
      let snapshot: CustomersSnapshotPayload;
      if (sinceIso == null) {
        // Pull completo: comparte el in-flight de loadWebCustomerSnapshot con
        // los readers del repo — sin esto cold-start dispara 2 RPCs paralelos.
        snapshot = await loadWebCustomerSnapshot(Boolean(opts?.force));
      } else {
        snapshot = await fetchRemoteCustomersSnapshot(sinceIso);
        // Incremental: merge sobre la cache completa; generation descarta pulls viejos.
        applyWebCustomerRemoteSnapshot(snapshot, pullGeneration);
      }
      remoteHadChanges = snapshot.has_changes !== false;

      if (snapshot.has_changes === false && sinceIso) {
        devLog('[fiadoPullApplication] has_changes:false, skip local apply');
        result = {
          data: {
            customersCount: 0,
            customerMovementsCount: 0,
            salePaymentAllocationsCount: 0,
          },
          source: 'remote',
        };
      } else {
        // ADR-017: RAM cache only — no SQLite mirror.
        result = { data: snapshotCounts(snapshot), source: 'remote' };
      }
    } catch (error) {
      devWarn('[fiadoPullApplication] Error al obtener snapshot desde Supabase, usando datos locales', error);
      if (opts?.force) {
        const message = error instanceof Error ? error.message : String(error);
        throw new SyncPullFailedError('fiado', `No se pudieron actualizar los datos de clientes: ${message}`);
      }
      result = {
        data: {
          customersCount: 0,
          customerMovementsCount: 0,
          salePaymentAllocationsCount: 0,
        },
        source: 'local',
      };
    }
  }

  if (opts?.force && result.source !== 'remote') {
    throw new SyncPullFailedError('fiado', 'No se pudieron actualizar los datos de clientes desde el servidor.');
  }

  if (result.source === 'remote') {
    recordSyncPullTimestamp(CUSTOMER_LAST_PULL_AT_KEY, new Date().toISOString());
    publishCustomerSynced({
      customersCount: result.data.customersCount,
      customerMovementsCount: result.data.customerMovementsCount,
      salePaymentAllocationsCount: result.data.salePaymentAllocationsCount,
    });
  }

  return result.source === 'remote' && remoteHadChanges;
}
